import React, { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ErrorMessage, FieldArray, Form, Formik, FormikHelpers } from "formik";
import { Button, Col, Row, Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ApolloError, useApolloClient, useReactiveVar } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import TextInputWithCounter from "../inputs/TextInputWithCounter";
import {
    CustomModal,
    SpinnerLoader,
    PriceInput,
    ReactSelect,
    Spinner,
    TextInput,
    TooltipCustom,
} from "@services/ui-components";
import TextareaInput from "../inputs/TextareaInput";
import CriticalResidueInput from "../inputs/CriticalResidueInput";
import {
    ALT_UNIT_GROUP_NONE_ID,
    clearAltErrors,
    clearAltFields,
    createProductFormValidationSchema,
    getAutoAltUnitId,
    getMainUnitGroup,
    getMainUnitOptions,
    getUnitLabelByValue,
    getUnitNameById,
    unitGroupQuantityId,
    unitPCId,
} from "../constants";
import ErrorService from "../../../services/ErrorService";
import { CategoryInterface, DuplicateProductData, unitGroupI } from "../types";
import NumberInput from "../inputs/NumberInput";
import { ServiceModalWrapper } from "@me-pos/service-modal";
import TextInputWithButton from "../inputs/TextInputWithButton";
import ImageCrop from "@services/ui-components/src/ImageCrop/ImageCrop";
import CategoryProduct from "../services/CategoryProduct";
import RequestInputService from "../services/RequestInputService";
import { normalizeCategoryData } from "../services/normalizeCategoryData";
import { refreshProductCategoriesCache } from "../services/refreshProductCategoriesCache";
import {
    GetBrandsDocument,
    useCreateProductCategoryMutation,
    useCreateProductMutation,
    useGeMainUnitsForCreateUpdateProductQuery,
    useGetAlternativeUnitGroupsForCreateUpdateProductQuery,
    useGetBrandsQuery,
    useGetProductCategoriesQuery,
    useGetProductsLazyQuery,
    useGetUnitsQuery,
} from "@me-team/host/main/products/graphql/products.hooks";
import {
    CompanyAccess,
    ProductCategoryNewInput,
    ProductCategoryNewProxy,
    ProductInput,
    ProductParametersProxy,
    UnitProxy,
} from "@me-team/host/main/graphql/types";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import WarehouseJoyrideWrapper from "../../Joyride/WarehouseTutorial/WarehouseJoyrideWrapper";
import {
    getCurrentStepFromLocalStorage,
    getWarehouseTutorialType,
    isWarehouseTutorialActive,
    moveStepInLocalStorage,
    StepDirection,
} from "../../Joyride/WarehouseTutorial/joyrideStepHelpers";
import { LOCAL_STORAGE_CREATED_ID } from "../../Joyride/WarehouseTutorial/constants";
import { Option } from "../../../interfaces/interfaces";
import CreateProductFormEffects from "../ProductsList/CreateProductFormEffects";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

interface ValidationError {
    [key: string]: { message: string; code: string | null }[];
}

const CreateProductForm: React.FC = () => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const navigate = useNavigate();
    const client = useApolloClient();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const canCreateProduct = useRoleAccess(CompanyAccess.ProductCreate);
    const canCreateProductCategory = useRoleAccess(CompanyAccess.ProductCategoryCreate);
    const buttonCategoryRef = useRef<HTMLButtonElement>(null);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [selectedMainUnitId, setSelectedMainUnitId] = useState<number>(null);
    const [relationsAlternativeUnits, setRelationsAlternativeUnits] = useState<UnitProxy[]>(null);
    const [isProductModalOpen, setIsProductModalOpen] = useState<boolean>(false);
    const [currentCategory, setCurrentCategory] = useState(null);
    const [categoryList, setCategoryList] = useState<CategoryInterface[]>(null);
    const [categoryInput, setCategoryInput] = useState(null);
    const [isDuplicateProduct, setIsDuplicateProduct] = useState<boolean>(false);
    const [pickedCategoryId, setPickedCategoryId] = useState<number>(null);
    const [duplicateProductData, setDuplicateProductData] = useState<DuplicateProductData | null>(null);
    const tutorialActive = isWarehouseTutorialActive();
    const tutorialType = getWarehouseTutorialType();
    const startStep = getCurrentStepFromLocalStorage();

    const [categoryText, setCategoryText] = useState([null, null]);
    const selectedCategory = categoryText[0] || "";
    const selectedSubcategory = categoryText[1] || "";

    const [createProductCategory] = useCreateProductCategoryMutation();
    const [createProduct] = useCreateProductMutation();

    const [, { refetch: productsRefetch }] = useGetProductsLazyQuery({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const {
        data: productCategoriesData,
        refetch: refetchCategories,
        loading: productCategoryLoading,
    } = useGetProductCategoriesQuery({
        variables: {
            itemsPerPage: 1000,
            delete: false,
            branchId: Number(branchId),
        },
        context: {
            errorType: "local",
        },
        onCompleted: (data) => {
            const normalizedItems = normalizeCategoryData(data);
            setCategoryList(CategoryProduct.categoryModuleCreator(normalizedItems, null, null));
            setCategoryInput(RequestInputService.createServiceCategoryInput(null, null, normalizedItems));
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const categoryListData: ProductCategoryNewProxy[] = productCategoriesData?.productCategories?.items;

    const { data: brandsData, loading: brandsLoading } = useGetBrandsQuery({
        variables: {
            companyId: +currentCompanyId,
            branchId: Number(branchId),
        },
        skip: !currentCompanyId,
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const suggestionsList: ProductParametersProxy[] = brandsData?.companyBrands;
    const handleSubmitCreateCategory = async (input: ProductCategoryNewInput) => {
        if (!canCreateProductCategory) return;

        try {
            currentCompanyId &&
                (await createProductCategory({
                    variables: {
                        companyId: +currentCompanyId,
                        input: input,
                        branchId: Number(branchId),
                    },
                    context: {
                        errorType: "local",
                    },
                    onCompleted: async (data) => {
                        data && setCurrentCategory(data.createProductCategoryNew);
                        await refreshProductCategoriesCache(client, branchId);
                    },
                    onError: (error) => {
                        setIsErrorModalOpen(ErrorService.errorHandling(error));
                    },
                }));
        } catch (error) {
            console.error(error);
        }
    };

    const handleOpenDuplicateOfProductModal = () => {
        setIsDuplicateProduct(true);
    };
    const handleCloseDuplicateOfProductModal = () => {
        setIsDuplicateProduct(false);
    };

    const handleLabelClick = (event: React.MouseEvent<HTMLLabelElement>) => {
        event.preventDefault();
        if (buttonCategoryRef.current) {
            buttonCategoryRef.current.focus();
        }
    };

    const handleCategoryNameChange = (categoryName: string) => {
        setCategoryText([categoryName]);
    };

    const handleSubCategoryNameChange = (subCategoryName: string, categoryName: string) => {
        setCategoryText([categoryName, subCategoryName]);
    };

    const { data: mainUnitsData, loading: mainUnitsLoading } = useGeMainUnitsForCreateUpdateProductQuery({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const { data: alternativeUnitGroupsData, refetch: alternativeUnitGroupsRefetch } =
        useGetAlternativeUnitGroupsForCreateUpdateProductQuery({
            variables: { mainUnitId: selectedMainUnitId },
            skip: !selectedMainUnitId,
            context: {
                errorType: "local",
            },
            onError: (error: ApolloError) => {
                setIsErrorModalOpen(ErrorService.errorHandling(error));
            },
        });
    const { data: unitsData } = useGetUnitsQuery({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const handleMainUnit = (
        selectedOption: Option,
        setFieldValue: FormikHelpers<ProductInput>["setFieldValue"],
        setFieldTouched: FormikHelpers<ProductInput>["setFieldTouched"],
        setFieldError: FormikHelpers<ProductInput>["setFieldError"]
    ) => {
        const nextMainUnitId: number = selectedOption.value;
        setFieldValue("unit", nextMainUnitId);

        setRelationsAlternativeUnits([]);
        clearAltFields(setFieldValue);
        setFieldTouched("alternativeUnitGroupInput.alternativeUnitGroup", false, false);
        setFieldTouched("alternativeUnitGroupInput.alternativeUnit", false, false);
        setFieldTouched("alternativeUnitGroupInput.alternativeValue", false, false);
        clearAltErrors(setFieldError);

        alternativeUnitGroupsRefetch();
    };

    const handleCancel = () => {
        tutorialActive && moveStepInLocalStorage(-1);
        navigate(`/warehouse/product/${currentCompanyId}/branch/${branchId}`);
    };

    const unitOptions: Option[] = getMainUnitOptions(mainUnitsData?.relations?.mainUnits);

    const alternativeUnitGroupOptions: Option[] = [
        ...((alternativeUnitGroupsData?.relations?.alternativeUnitGroups ?? []).map((unitGroup: unitGroupI) => ({
            value: unitGroup.id,
            label: unitGroup.name,
        })) as Option[]),
        { value: ALT_UNIT_GROUP_NONE_ID, label: t("none") },
    ];

    const handleAlternativeUnitGroupChange = (
        selectedOption: Option,
        setFieldValue: FormikHelpers<ProductInput>["setFieldValue"],
        setFieldTouched: FormikHelpers<ProductInput>["setFieldTouched"],
        setFieldError: FormikHelpers<ProductInput>["setFieldError"],
        mainUnitId: number | null
    ) => {
        const nextGroupId =
            selectedOption?.value !== undefined && selectedOption?.value !== null ? Number(selectedOption.value) : null;

        setFieldValue("alternativeUnitGroupInput.alternativeUnitGroup", nextGroupId, false);

        clearAltErrors(setFieldError);

        if (nextGroupId === ALT_UNIT_GROUP_NONE_ID) {
            const autoUnitId = getAutoAltUnitId(mainUnitId);
            setRelationsAlternativeUnits([]);

            setFieldValue("alternativeUnitGroupInput.alternativeUnit", autoUnitId, false);
            setFieldValue("alternativeUnitGroupInput.alternativeValue", 1, false);
            setFieldTouched("alternativeUnitGroupInput.alternativeUnitGroup", false, false);
            setFieldTouched("alternativeUnitGroupInput.alternativeUnit", false, false);
            setFieldTouched("alternativeUnitGroupInput.alternativeValue", false, false);

            return;
        }

        const filteredAlternativeUnits =
            unitsData?.relations?.units?.filter((unit) => unit.unitGroup.id === nextGroupId) || [];

        setRelationsAlternativeUnits(filteredAlternativeUnits);

        if (filteredAlternativeUnits.length > 0) {
            setFieldValue("alternativeUnitGroupInput.alternativeUnit", filteredAlternativeUnits[0].id, false);
        } else {
            setFieldValue("alternativeUnitGroupInput.alternativeUnit", null, false);
        }

        setFieldValue("alternativeUnitGroupInput.alternativeValue", null, false);

        setFieldError("alternativeUnitGroupInput.alternativeUnitGroup", undefined);
        setFieldError("alternativeUnitGroupInput.alternativeUnit", undefined);
        setFieldError("alternativeUnitGroupInput.alternativeValue", undefined);
    };

    const handleAlternativeUnitChange = (
        selectedOption: Option,
        setFieldValue: FormikHelpers<ProductInput>["setFieldValue"]
    ) => {
        setFieldValue("alternativeUnitGroupInput.alternativeUnit", selectedOption.value);
    };

    const initialValues: ProductInput = {
        logo: null,
        name: "",
        brand: "",
        category: null,
        sku: "",
        barcode: "",
        description: "",
        unitGroup: unitGroupQuantityId,
        unit: unitPCId,
        criticalResidue: null,
        alternativeUnitGroupInput: {
            alternativeUnitGroup: null,
            alternativeUnit: null,
            alternativeValue: null,
        },
        productAttributes: [],
        isUpdateLogo: false,
        cost: null,
        price: null,
    };

    const handleCreateSubmit = async (values: ProductInput) => {
        if (!canCreateProduct) return;

        const isNone = values?.alternativeUnitGroupInput?.alternativeUnitGroup === ALT_UNIT_GROUP_NONE_ID;

        const input: ProductInput = {
            name: values?.name,
            brand: values?.brand,
            category: values?.category,
            sku: values?.sku || null,
            barcode: values?.barcode || null,
            description: values?.description || null,
            unitGroup: getMainUnitGroup(values?.unit),
            unit: values?.unit,
            criticalResidue: values?.criticalResidue || null,
            alternativeUnitGroupInput: isNone
                ? null
                : values?.alternativeUnitGroupInput?.alternativeUnitGroup == null &&
                    values?.alternativeUnitGroupInput?.alternativeUnit == null &&
                    values?.alternativeUnitGroupInput?.alternativeValue == null
                  ? null
                  : {
                        alternativeUnitGroup: values?.alternativeUnitGroupInput?.alternativeUnitGroup,
                        alternativeUnit: values?.alternativeUnitGroupInput?.alternativeUnit,
                        alternativeValue: values?.alternativeUnitGroupInput?.alternativeValue,
                    },
            productAttributes: values?.productAttributes ? values?.productAttributes : null,
            isUpdateLogo: false,
            cost: values?.cost,
            price: values?.price,
        };
        if (values?.isUpdateLogo === true) input.logo = values.logo;

        await createProduct({
            variables: { companyId: +currentCompanyId, input, branchId: Number(branchId) },
            context: {
                errorType: "local",
            },
            refetchQueries: [
                {
                    query: GetBrandsDocument,
                    variables: {
                        companyId: Number(currentCompanyId),
                        branchId: Number(branchId),
                    },
                },
            ],
            onCompleted: async (data) => {
                await refreshProductCategoriesCache(client, branchId);
                currentCompanyId &&
                    productsRefetch({
                        companyId: Number(currentCompanyId),
                        branchId: Number(branchId),
                    });
                tutorialActive && localStorage.setItem(LOCAL_STORAGE_CREATED_ID, String(data.createProduct.id));
                tutorialActive && moveStepInLocalStorage(StepDirection.NEXT);
                data &&
                    navigate(`/warehouse/product/${currentCompanyId}/branch/${branchId}`, {
                        state: { text: t("Created"), toast: true, dataTure: "product-created" },
                    });
            },
            onError: (error: ApolloError) => {
                console.error("error", error?.graphQLErrors[0]);
                const validationErrors = (error?.graphQLErrors[0]?.extensions as { validation?: ValidationError })
                    ?.validation;

                const extractedData: Record<string, string> = {};

                if (validationErrors && validationErrors[""]) {
                    validationErrors[""]?.forEach((err: any) => {
                        const message: string = err.message;

                        if (message.includes("The product already exists with")) {
                            const regex = /name\s*=\s*"(.+?)"\s*,\s*brand\s*=\s*"(.+?)"\s*and\s*id\s*=\s*"(.+?)"/;
                            const match = message.match(regex);

                            if (match) {
                                const [, name, brand, id] = match;
                                extractedData.name = name;
                                extractedData.brand = brand;
                                extractedData.productId = id;
                                setDuplicateProductData(extractedData);
                                handleOpenDuplicateOfProductModal();
                            }
                        }
                    });
                } else {
                    setIsErrorModalOpen(ErrorService.errorHandling(error));
                }
            },
        });
    };

    const handlePhoto = (setFieldValue: any) => {
        setFieldValue("isUpdateLogo", true);
    };

    const handleNavigateAndClose = () => {
        handleCloseDuplicateOfProductModal();
        navigate(
            `/warehouse/product/${currentCompanyId}/${duplicateProductData?.productId}/edit-product/branch/${branchId}`
        );
    };

    if (brandsLoading || productCategoryLoading || !currentCompanyId || mainUnitsLoading) return <Spinner />;

    return (
        <>
            <Formik
                initialValues={initialValues}
                validationSchema={createProductFormValidationSchema(t)}
                onSubmit={handleCreateSubmit}
            >
                {({ setFieldValue, values, touched, errors, setFieldTouched, isSubmitting, setFieldError }) => {
                    const selectedGroupId = values?.alternativeUnitGroupInput?.alternativeUnitGroup;
                    const isAltNoneSelected = selectedGroupId === ALT_UNIT_GROUP_NONE_ID;
                    const isGroupNull = selectedGroupId == null;
                    let alternativeUnitOptions: Option[] = [];
                    if (isAltNoneSelected) {
                        const id = getAutoAltUnitId(values.unit);
                        const label = getUnitNameById(id, unitsData?.relations?.units);
                        alternativeUnitOptions = [{ value: id, label }];
                    } else {
                        alternativeUnitOptions =
                            relationsAlternativeUnits?.map((unit: unitGroupI) => {
                                return {
                                    value: unit?.id,
                                    label: unit?.name,
                                };
                            }) || [];
                    }

                    const disableAltFields = isAltNoneSelected || isGroupNull;

                    return (
                        <>
                            <CreateProductFormEffects
                                pickedCategoryId={pickedCategoryId}
                                setSelectedMainUnitId={setSelectedMainUnitId}
                            />
                            <Form data-tour="create-product-form">
                                <Row className="container-max-widths-xxl flex-column flex-lg-row  mb-5">
                                    <Col lg={9} xxl={9} className="pe-lg-5">
                                        <h4 className="mb-4">{t("Information")}</h4>
                                        <Row className="mb-3 mb-sm-4 fs-7">
                                            <TextInputWithCounter
                                                id="addProduct-name-field"
                                                label={t("Product Name")}
                                                name="name"
                                                placeholder={t("Enter the Product Name")}
                                            />
                                        </Row>
                                        <Row className="mb-3 mb-sm-4 fs-7">
                                            <TextInputWithCounter
                                                id="addProduct-brand-field"
                                                label={t("Brand")}
                                                name="brand"
                                                placeholder={t("Enter a Brand")}
                                                suggestions={suggestionsList}
                                            />
                                        </Row>
                                        <Row className="mb-3 mb-sm-4 fs-7">
                                            <TextInput
                                                id="addProduct-sku-field"
                                                name="sku"
                                                placeholder="Enter a SKU"
                                                label={t("ProductsList.SKU")}
                                                maxLength={50}
                                                labelCol={2}
                                                inputCol={10}
                                            />
                                        </Row>
                                        <Row className="mb-3 mb-sm-4 fs-7">
                                            <TextInput
                                                id="addProduct-barcode-field"
                                                name="barcode"
                                                placeholder="Enter a Barcode"
                                                label={t("ProductsList.Barcode")}
                                                maxLength={20}
                                            />
                                        </Row>
                                        <Row className="mb-sm-4">
                                            <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                <label
                                                    htmlFor="addProduct-category-field"
                                                    className="text-dark fs-7 fw-bold py-1 required-label"
                                                    onClick={handleLabelClick}
                                                >
                                                    {t("Category")}
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <div>
                                                    <span
                                                        className={`${selectedCategory ? "me-4" : "d-none"}  mb-2 mb-lg-0 text-dark`}
                                                    >
                                                        {selectedCategory}{" "}
                                                        {selectedSubcategory ? (
                                                            <>
                                                                <i className="bi bi-arrow-right"></i>{" "}
                                                                {selectedSubcategory}
                                                            </>
                                                        ) : null}
                                                    </span>
                                                    <Button
                                                        ref={buttonCategoryRef}
                                                        variant={`${touched.category && errors.category ? "outline-danger" : "outline-primary"}`}
                                                        className="w-100 fw-normal py-1"
                                                        id="addProduct-category-field"
                                                        style={{ maxWidth: "149px" }}
                                                        onClick={() => {
                                                            setIsProductModalOpen(true);
                                                        }}
                                                    >
                                                        {categoryText[0] ? t("Change Category") : t("Select Category")}
                                                    </Button>
                                                    <ErrorMessage name="category">
                                                        {(msg) => <div className="text-danger fs-7 mt-1">{msg}</div>}
                                                    </ErrorMessage>
                                                </div>
                                            </Col>
                                        </Row>
                                        <Row className="mb-3 mb-sm-4 fs-7">
                                            <TextareaInput
                                                label={t("Description")}
                                                name="description"
                                                id="addProduct-description-field"
                                                placeholder={t("Enter a Description")}
                                            />
                                        </Row>
                                        <Row className="mb-3 mb-sm-4 fs-7">
                                            <CriticalResidueInput
                                                label={t("Critical Residue")}
                                                name="criticalResidue"
                                                id="addProduct-criticalResidue-field"
                                                placeholder={t("Enter a Critical Residue")}
                                                maxLength={9}
                                                textTooltip={t(
                                                    "The number of products in stock, upon reaching which a warning email will be sent."
                                                )}
                                            />
                                        </Row>
                                        <Row className="mb-3 mb-sm-4">
                                            <Col sm={3} md={2} className="mb-2 mb-sm-0 d-flex">
                                                <label
                                                    htmlFor="addProduct-unit-field"
                                                    className="text-dark fs-7 fw-bold"
                                                >
                                                    <span className="required-label">
                                                        {t("Unit of Measure for sale")}
                                                    </span>
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <Row className="gap-4 gap-xxl-0">
                                                    <Col xxl={2}>
                                                        <ReactSelect
                                                            id="addProduct-unit-field"
                                                            name="unit"
                                                            placeholder={t("Select an unit")}
                                                            options={unitOptions}
                                                            value={unitOptions?.find(
                                                                (option: Option) => option.value === values?.unit
                                                            )}
                                                            onChange={(selectedOption: Option) =>
                                                                handleMainUnit(
                                                                    selectedOption,
                                                                    setFieldValue,
                                                                    setFieldTouched,
                                                                    setFieldError
                                                                )
                                                            }
                                                            isSearchable={false}
                                                        />
                                                        <ErrorMessage name="unit">
                                                            {(msg) => (
                                                                <div className="invalid-feedback d-block">{msg}</div>
                                                            )}
                                                        </ErrorMessage>
                                                    </Col>
                                                    <Col xxl={5} className="mb-2 mb-md-0 d-flex">
                                                        <Col
                                                            xs={6}
                                                            sm={5}
                                                            xxl={6}
                                                            className="d-flex justify-content-xxl-end"
                                                        >
                                                            <label
                                                                htmlFor="addProduct-cost-field"
                                                                className="text-dark fs-7 fw-bold me-3 h-100 d-flex align-items-center"
                                                                style={{ maxHeight: "49px" }}
                                                            >
                                                                <span>
                                                                    {t("Cost price 1 {{unit}}", {
                                                                        unit: t(
                                                                            getUnitLabelByValue(
                                                                                unitOptions,
                                                                                values?.unit,
                                                                                "costPrice"
                                                                            )
                                                                        ),
                                                                    })}
                                                                </span>
                                                            </label>
                                                        </Col>
                                                        <Col xs={6} sm={7} xxl={6}>
                                                            <PriceInput
                                                                name="cost"
                                                                placeholder="0.00"
                                                                id="addProduct-cost-field"
                                                            />
                                                        </Col>
                                                    </Col>
                                                    <Col xxl={5} className="mb-2 mb-md-0 d-flex">
                                                        <Col
                                                            xs={6}
                                                            sm={5}
                                                            xxl={6}
                                                            className="d-flex justify-content-xxl-end"
                                                        >
                                                            <label
                                                                htmlFor="addProduct-price-field"
                                                                className="text-dark fs-7 fw-bold me-3 h-100 d-flex-column align-items-center"
                                                                style={{ maxHeight: "49px" }}
                                                            >
                                                                <span className="required-label">
                                                                    {t("Selling price")}
                                                                </span>
                                                                <span className="d-block">
                                                                    {t(
                                                                        getUnitLabelByValue(
                                                                            unitOptions,
                                                                            values?.unit,
                                                                            "sellingPrice"
                                                                        )
                                                                    )}
                                                                </span>
                                                            </label>
                                                        </Col>
                                                        <Col xs={6} sm={7} xxl={6}>
                                                            <PriceInput
                                                                name="price"
                                                                placeholder="0.00"
                                                                id="addProduct-price-field"
                                                            />
                                                        </Col>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                        <Row className="mb-3 mb-sm-4">
                                            <Col sm={3} md={2} className="mb-2 mb-sm-0 d-flex">
                                                <label
                                                    htmlFor="addProduct-alternativeUnitGroup-field"
                                                    className="text-dark fs-7 fw-bold text-break"
                                                >
                                                    <span className="required-label">
                                                        {t("Alternative measuring unit")}
                                                    </span>
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <Row className="g-4">
                                                    <Col xxl={5}>
                                                        <ReactSelect
                                                            id="addProduct-alternativeUnitGroup-field"
                                                            name="alternativeUnitGroupInput.alternativeUnitGroup"
                                                            placeholder={t("Select type of measure")}
                                                            options={alternativeUnitGroupOptions}
                                                            value={
                                                                selectedGroupId == null
                                                                    ? null
                                                                    : (alternativeUnitGroupOptions?.find(
                                                                          (option) => option.value === selectedGroupId
                                                                      ) ?? null)
                                                            }
                                                            onChange={(selectedOption: Option) =>
                                                                handleAlternativeUnitGroupChange(
                                                                    selectedOption,
                                                                    setFieldValue,
                                                                    setFieldTouched,
                                                                    setFieldError,
                                                                    values?.unit
                                                                )
                                                            }
                                                            isSearchable={false}
                                                        />
                                                        <div>
                                                            <span className="fs-7 text-grey">
                                                                {t("for ex. weight")}
                                                            </span>
                                                        </div>
                                                        <ErrorMessage name="alternativeUnitGroupInput.alternativeUnitGroup">
                                                            {(msg) => (
                                                                <div className="invalid-feedback d-block">{msg}</div>
                                                            )}
                                                        </ErrorMessage>
                                                    </Col>
                                                    <Col xxl={4} className="mb-2 mb-md-0 d-flex">
                                                        <label
                                                            htmlFor="alternativeUnitGroupInput.alternativeValue"
                                                            className="text-dark fs-7 fw-bold me-3 h-100 d-flex align-items-center"
                                                            style={{ whiteSpace: "nowrap", maxHeight: "49px" }}
                                                        >
                                                            <span>
                                                                {t(
                                                                    getUnitLabelByValue(
                                                                        unitOptions,
                                                                        values?.unit,
                                                                        "quantity"
                                                                    )
                                                                )}
                                                            </span>
                                                        </label>
                                                        <NumberInput
                                                            id="addProduct-alternativeUnitGroupInput.alternativeValue-field"
                                                            name="alternativeUnitGroupInput.alternativeValue"
                                                            placeholder={t("Add quantity")}
                                                            disabled={disableAltFields}
                                                            maxLength={9}
                                                        />
                                                    </Col>
                                                    <Col xxl={3} className="mb-2 mb-md-0">
                                                        <ReactSelect
                                                            id="addProduct-alternativeUnit-field"
                                                            name="alternativeUnitGroupInput.alternativeUnit"
                                                            placeholder={t("Select an unit")}
                                                            options={alternativeUnitOptions}
                                                            value={
                                                                alternativeUnitOptions?.length > 0
                                                                    ? alternativeUnitOptions.find(
                                                                          (option: Option) =>
                                                                              option.value ===
                                                                              values?.alternativeUnitGroupInput
                                                                                  ?.alternativeUnit
                                                                      )
                                                                    : null
                                                            }
                                                            onChange={(selectedOption: Option) =>
                                                                handleAlternativeUnitChange(
                                                                    selectedOption,
                                                                    setFieldValue
                                                                )
                                                            }
                                                            isSearchable={false}
                                                            disabled={disableAltFields}
                                                        />
                                                        <ErrorMessage name="alternativeUnitGroupInput.alternativeUnit">
                                                            {(msg) => (
                                                                <div className="invalid-feedback d-block">{msg}</div>
                                                            )}
                                                        </ErrorMessage>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                        <Row className="pt-6 mb-3 mb-sm-4">
                                            <Col>
                                                <Row className="d-flex align-items-center justify-content-between">
                                                    <Col xs={6} md={5}>
                                                        <h5 className="mb-0">
                                                            {t("Additional product attributes")}
                                                            <TooltipCustom
                                                                placement="right"
                                                                text={t(
                                                                    "You can add your own product" +
                                                                        " properties, for example: Color, Tone, Used with, etc."
                                                                )}
                                                            >
                                                                <i className="bi bi-info-circle fs-16 text-dark ms-3"></i>
                                                            </TooltipCustom>
                                                        </h5>
                                                    </Col>
                                                    <Col xs={6} md={2} className="d-flex justify-content-end">
                                                        <TooltipCustom placement="bottom" text={t("Add attribute")}>
                                                            <Button
                                                                variant="outline-secondary"
                                                                className="btn-square px-3 py-6"
                                                                onClick={() =>
                                                                    setFieldValue("productAttributes", [
                                                                        ...values.productAttributes,
                                                                        { name: "", property: "" },
                                                                    ])
                                                                }
                                                            >
                                                                <i className="bi bi-plus-lg text-dark"></i>
                                                            </Button>
                                                        </TooltipCustom>
                                                    </Col>
                                                </Row>
                                                {values?.productAttributes?.length > 0 ? (
                                                    <Row className="mt-3">
                                                        <Col>
                                                            <FieldArray
                                                                name="productAttributes"
                                                                render={({ remove }) => (
                                                                    <>
                                                                        {values?.productAttributes?.map(
                                                                            (_: any, index: number) => {
                                                                                return (
                                                                                    <div
                                                                                        key={index}
                                                                                        className="d-flex flex-column mb-3"
                                                                                    >
                                                                                        <Row className="mb-3 mb-sm-4 fs-7">
                                                                                            <TextInput
                                                                                                id={`addProduct-productAttributes.${index}.name-field`}
                                                                                                name={`productAttributes.${index}.name`}
                                                                                                placeholder={t(
                                                                                                    "Attributes.Add Attribute"
                                                                                                )}
                                                                                                label={t(
                                                                                                    "Additional Attribute"
                                                                                                )}
                                                                                                required={true}
                                                                                                maxLength={20}
                                                                                                infoText="for ex. color"
                                                                                            />
                                                                                        </Row>
                                                                                        <TextInputWithButton
                                                                                            id={`addProduct-productAttributes.${index}.name-field`}
                                                                                            name={`productAttributes.${index}.property`}
                                                                                            index={index}
                                                                                            remove={remove}
                                                                                            label={t("Property")}
                                                                                            infoText="for ex. blue"
                                                                                        />
                                                                                    </div>
                                                                                );
                                                                            }
                                                                        )}
                                                                    </>
                                                                )}
                                                            />
                                                        </Col>
                                                    </Row>
                                                ) : null}
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col lg={4} xxl={3}>
                                        <div className="d-flex w-100 flex-column  justify-content-lg-end">
                                            <h4 className="mb-3 w-100">{t("Product's photo")}</h4>
                                            <ImageCrop
                                                setFieldValue={setFieldValue}
                                                fieldName="logo"
                                                modalTitle={t("Product's photo")}
                                                notBGinfo={true}
                                                fieldSetter={() => handlePhoto(setFieldValue)}
                                            />
                                            <ErrorMessage name="logo">{(msg) => <div>{msg}</div>}</ErrorMessage>
                                        </div>
                                    </Col>
                                </Row>
                                <div className="d-sm-flex my-4 mt-lg-0">
                                    <Button
                                        type="button"
                                        variant="outline-primary"
                                        className="custom-btn mb-3 mb-sm-0 me-3"
                                        onClick={handleCancel}
                                    >
                                        {t("cancel")}
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        className="custom-btn fw-bold"
                                        disabled={isSubmitting || !canCreateProduct}
                                    >
                                        {isSubmitting ? (
                                            <SpinnerLoader variant="light" loadingText={t("Adding...")} />
                                        ) : (
                                            t("Add")
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </>
                    );
                }}
            </Formik>
            {isProductModalOpen && productCategoriesData && !productCategoryLoading ? (
                <ServiceModalWrapper
                    companyId={Number(currentCompanyId)}
                    branchId={Number(branchId)}
                    i18n={i18next}
                    selectedCategoryId={null}
                    selectedSubcategoryId={null}
                    apolloUrl={process.env.REACT_APP_API_URL}
                    isOpen={isProductModalOpen}
                    onClose={() => {
                        setIsProductModalOpen(!isProductModalOpen);
                    }}
                    onCategorySelect={(selectedCategory) => {
                        handleCategoryNameChange(selectedCategory?.name);
                        setPickedCategoryId(selectedCategory?.id);
                        setIsProductModalOpen(!isProductModalOpen);
                    }}
                    onSubcategorySelect={(selectedSubcategory) => {
                        handleSubCategoryNameChange(
                            selectedSubcategory?.name,
                            selectedSubcategory?.parentCategory?.name
                        );
                        setPickedCategoryId(selectedSubcategory?.id);
                        setIsProductModalOpen(!isProductModalOpen);
                    }}
                    categoryList={categoryList}
                    categoryInput={categoryInput}
                    categoryListData={categoryListData}
                    refetchCategories={refetchCategories}
                    onSubmitCreate={handleSubmitCreateCategory}
                    canCreateCategory={canCreateProductCategory}
                    currentCategory={currentCategory}
                    categoryService={CategoryProduct}
                    requestInputService={RequestInputService}
                    normalizeCategoryData={normalizeCategoryData}
                />
            ) : null}
            <CustomModal
                show={isDuplicateProduct}
                handleClose={handleCloseDuplicateOfProductModal}
                title={
                    <Container>
                        <Row>
                            <h5 className="m-0 p-0 fw-normal">{t("The product already exists")}</h5>
                        </Row>
                    </Container>
                }
                body={
                    <Container>
                        <Row>
                            <Col>
                                <span className="fs-16 me-1">
                                    {t(
                                        "Such a product already exists, you can" +
                                            " familiarize yourself with it at the link"
                                    )}
                                </span>
                                <div>
                                    <Button
                                        variant="link"
                                        className="fs-normal text-primary text-decoration-underline cursor-pointer"
                                        onClick={handleNavigateAndClose}
                                    >
                                        {`${duplicateProductData?.brand} ${duplicateProductData?.name}`}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                }
                buttons={[
                    {
                        text: t("ProductDuplicate.Back"),
                        onClick: handleCloseDuplicateOfProductModal,
                        variant: "outline-primary",
                        className: "w-100 fw-normal",
                    },
                ]}
            />

            {tutorialActive && tutorialType && (
                <WarehouseJoyrideWrapper tutorialType={tutorialType} startStep={startStep} />
            )}

            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};

export default CreateProductForm;
